import React from 'react';
import { Stack, Typography, Button, Box } from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Inbox as InboxIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

export interface EmptyStateProps {
  /**
   * Main title for the empty state
   */
  title: string;
  /**
   * Optional description text
   */
  description?: string;
  /**
   * Optional icon to display (defaults to InboxIcon)
   */
  icon?: React.ReactNode;
  /**
   * Primary action button configuration
   */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    disabled?: boolean;
  };
  /**
   * Secondary action button configuration
   */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Variant controls the visual presentation
   * - 'default': Standard empty state with icon
   * - 'search': Empty search results state
   * - 'minimal': Compact version without icon
   */
  variant?: 'default' | 'search' | 'minimal';
  /**
   * Size of the empty state
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Additional children to render below the description
   */
  children?: React.ReactNode;
}

const sizeStyles = {
  small: {
    iconSize: 40,
    titleVariant: 'subtitle2' as const,
    descVariant: 'caption' as const,
    padding: 3,
    spacing: 1,
  },
  medium: {
    iconSize: 56,
    titleVariant: 'subtitle1' as const,
    descVariant: 'body2' as const,
    padding: 6,
    spacing: 2,
  },
  large: {
    iconSize: 72,
    titleVariant: 'h6' as const,
    descVariant: 'body1' as const,
    padding: 8,
    spacing: 3,
  },
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  variant = 'default',
  size = 'medium',
  children,
}) => {
  const styles = sizeStyles[size];

  const getDefaultIcon = () => {
    if (variant === 'search') {
      return <SearchIcon sx={{ fontSize: styles.iconSize, color: 'text.disabled' }} />;
    }
    return <InboxIcon sx={{ fontSize: styles.iconSize, color: 'text.disabled' }} />;
  };

  const renderIcon = () => {
    if (variant === 'minimal') return null;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: styles.iconSize + 24,
          height: styles.iconSize + 24,
          borderRadius: '50%',
          bgcolor: 'action.hover',
          mb: 2,
        }}
      >
        {icon || getDefaultIcon()}
      </Box>
    );
  };

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={styles.spacing}
      sx={{
        py: styles.padding,
        px: 3,
        textAlign: 'center',
      }}
    >
      {renderIcon()}

      <Stack spacing={1} alignItems="center" sx={{ maxWidth: 400 }}>
        <Typography variant={styles.titleVariant} fontWeight={600} color="text.primary">
          {title}
        </Typography>

        {description && (
          <Typography variant={styles.descVariant} color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {description}
          </Typography>
        )}
      </Stack>

      {children}

      {(primaryAction || secondaryAction) && (
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          {primaryAction && (
            <Button
              variant="contained"
              color="primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              startIcon={primaryAction.icon || <AddIcon />}
              size={size === 'small' ? 'small' : 'medium'}
            >
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outlined"
              color="primary"
              onClick={secondaryAction.onClick}
              size={size === 'small' ? 'small' : 'medium'}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};

/**
 * Preset empty states for common use cases
 */
export const EmptyStatePresets = {
  /**
   * Empty state for no tools
   */
  NoTools: (props: { onCreateTool?: () => void }) => (
    <EmptyState
      title="No tools yet"
      description="Create your first tool to start building AI-powered workflows with your data."
      primaryAction={
        props.onCreateTool
          ? {
              label: 'Create Tool',
              onClick: props.onCreateTool,
            }
          : undefined
      }
    />
  ),

  /**
   * Empty state for no toolsets
   */
  NoToolsets: (props: { onCreateToolset?: () => void }) => (
    <EmptyState
      title="No toolsets yet"
      description="Create a toolset to group related tools together and publish them as an MCP server."
      primaryAction={
        props.onCreateToolset
          ? {
              label: 'Create Toolset',
              onClick: props.onCreateToolset,
            }
          : undefined
      }
    />
  ),

  /**
   * Empty state for no MCP servers
   */
  NoMCPServers: (props: { onCreateServer?: () => void }) => (
    <EmptyState
      title="No MCP servers yet"
      description="Create an MCP server to expose your toolsets to AI assistants like Claude and Cursor."
      primaryAction={
        props.onCreateServer
          ? {
              label: 'Create MCP Server',
              onClick: props.onCreateServer,
            }
          : undefined
      }
    />
  ),

  /**
   * Empty state for no MCP flows
   */
  NoMCPFlows: (props: { onOpenStudio?: () => void }) => (
    <EmptyState
      title="No MCP flows yet"
      description="Flows show up here automatically when you build an MCP server from a connector credential in MCP Studio."
      primaryAction={
        props.onOpenStudio
          ? {
              label: 'Open MCP Studio',
              onClick: props.onOpenStudio,
              icon: <OpenInNewIcon />,
            }
          : undefined
      }
    />
  ),

  /**
   * Empty search results
   */
  NoSearchResults: (props: {
    searchQuery?: string;
    description?: string;
    onClearSearch?: () => void;
  }) => (
    <EmptyState
      title="No results found"
      description={
        props.description
          ? props.description
          : props.searchQuery
            ? `No items match "${props.searchQuery}". Try a different search term.`
            : 'No items match your search criteria.'
      }
      variant="search"
      secondaryAction={
        props.onClearSearch
          ? {
              label: 'Clear Search',
              onClick: props.onClearSearch,
            }
          : undefined
      }
    />
  ),

  /**
   * Empty state for no items in a selection list
   */
  NoItemsToSelect: (props: { itemType?: string }) => (
    <EmptyState
      title={`No ${props.itemType || 'items'} available`}
      description={`Create some ${props.itemType || 'items'} first to add them here.`}
      variant="minimal"
      size="small"
    />
  ),
};

export default EmptyState;
